/**
 * AgentCredentialsList — credenciales del agente (estándar de listados del CMDB).
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Dos tablas propias (`akm-table`) con su CSS (`AgentApiKeysManager.css`), un
 * modal de confirmación propio, cabecera duplicada y un bloque enorme de ayuda
 * con comandos dentro de la propia pantalla. Sin buscador, sin filtros, sin
 * columnas elegibles y sin export.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * `ListPage` con el listado de **credenciales individuales de agente** (que es el
 * inventario de verdad: cada una dice a qué dispositivo pertenece) y las **claves
 * de empresa** en un diálogo, que son un catálogo pequeño y global —el mismo
 * patrón que «Tipos de subred» en el IPAM—.
 *
 * Nota sobre el borrado: en este modelo no todo es «eliminar».
 *  · **Revocar** (soft delete) deja la credencial inactiva y conserva el rastro.
 *  · **Borrar** solo está disponible para las ya revocadas, y es definitivo.
 * Por eso van como acción propia y como borrado, respectivamente, en vez de
 * ofrecer dos veces lo mismo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyIcon,
  ListBulletIcon,
  Squares2X2Icon,
  NoSymbolIcon,
  ServerStackIcon,
  PlusIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import { docElementPath } from '../utils/docElements';
import { hace } from '../utils/relativeTime';
import api from '../services/api';

export default function AgentCredentialsList({ companyId }) {
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState([]);
  const [companyKeys, setCompanyKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');

  const [filterStatus, setFilterStatus] = useState('');
  const [filterLinked, setFilterLinked] = useState('');
  const [filterCiType, setFilterCiType] = useState('');
  const [groupMode, setGroupMode] = useState('');

  const [keysOpen, setKeysOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [rawKey, setRawKey] = useState('');
  const [pendingAction, setPendingAction] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [credsRes, keysRes] = await Promise.all([
        api.getAgentCredentials(companyId),
        api.getAgentApiKeys(companyId),
      ]);
      setCredentials(Array.isArray(credsRes?.credentials) ? credsRes.credentials : []);
      setCompanyKeys(Array.isArray(keysRes?.api_keys) ? keysRes.api_keys : []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las credenciales');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const aviso = (texto) => {
    setNotice(texto);
    setTimeout(() => setNotice(''), 3000);
  };

  /* ── acciones sobre credenciales ────────────────────────────────────────── */
  const revocarCredencial = async (c) => {
    setPendingAction(`revoke-${c.id}`);
    try {
      await api.revokeAgentCredential(companyId, c.id, 'Revocada desde el panel del agente');
      aviso('Credencial revocada');
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo revocar');
    } finally {
      setPendingAction('');
    }
  };

  const borrarCredencial = async (c) => {
    try {
      await api.deleteAgentCredential(companyId, c.id, 'Purga de credencial revocada');
      aviso('Credencial borrada');
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo borrar');
    }
  };

  /* ── claves de empresa ─────────────────────────────────────────────────── */
  const crearClave = async () => {
    if (!newKeyName.trim() || creatingKey) return;
    setCreatingKey(true);
    setError('');
    try {
      const res = await api.createAgentApiKey(companyId, newKeyName.trim());
      setRawKey(res.raw_key || '');
      setNewKeyName('');
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo crear la clave');
    } finally {
      setCreatingKey(false);
    }
  };

  const revocarClave = async (key) => {
    setPendingAction(`key-revoke-${key.id}`);
    try {
      await api.revokeAgentApiKey(companyId, key.id);
      aviso('Clave de empresa revocada');
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo revocar la clave');
    } finally {
      setPendingAction('');
    }
  };

  const borrarClave = async (key) => {
    setPendingAction(`key-delete-${key.id}`);
    try {
      await api.deleteAgentApiKey(companyId, key.id);
      aviso('Clave de empresa borrada');
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo borrar la clave');
    } finally {
      setPendingAction('');
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const ciTypeLabel = (tipo) => {
    const mapa = {
      server: 'Servidor', workstation: 'Estación de trabajo', virt_vm: 'Máquina virtual',
      printer: 'Impresora', wifi_controller: 'Controlador WiFi', firewall: 'Firewall',
      router: 'Router', ip_phone: 'Teléfono IP',
    };
    return mapa[tipo] || tipo || 'Sin tipo';
  };

  const ciTypeOptions = useMemo(() => {
    const tipos = [...new Set(credentials.map((c) => c.association?.ci_type || c.ci_type).filter(Boolean))];
    return tipos.sort().map((t) => ({ value: t, label: ciTypeLabel(t) }));
  }, [credentials]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return credentials.filter((c) => {
      if (filterStatus === 'active' && !c.is_active) return false;
      if (filterStatus === 'revoked' && c.is_active) return false;
      const tipo = c.association?.ci_type || c.ci_type;
      const vinculada = Boolean(c.association?.ci_id || c.ci_id);
      if (filterLinked === 'yes' && !vinculada) return false;
      if (filterLinked === 'no' && vinculada) return false;
      if (filterCiType && tipo !== filterCiType) return false;
      if (!term) return true;
      return [c.name, c.agent_id, c.key_prefix, c.association?.display, ciTypeLabel(tipo)]
        .filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [credentials, search, filterStatus, filterLinked, filterCiType]);

  const clearFilters = () => {
    setFilterStatus(''); setFilterLinked(''); setFilterCiType(''); setSearch('');
  };

  const cuenta = useMemo(() => ({
    total: credentials.length,
    activas: credentials.filter((c) => c.is_active).length,
    revocadas: credentials.filter((c) => !c.is_active).length,
    vinculadas: credentials.filter((c) => c.association?.ci_id || c.ci_id).length,
  }), [credentials]);

  const kpis = (
    <div className="list-kpis list-kpis--chips">
      <button type="button" className="list-kpi-chip" onClick={clearFilters} title="Ver todas las credenciales">
        <span className="list-kpi-value">{cuenta.total}</span>
        <span className="list-kpi-label">credenciales</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('active'); }} title="Ver las activas">
        <span className="list-kpi-value">{cuenta.activas}</span>
        <span className="list-kpi-label">activas</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('revoked'); }} title="Ver las revocadas">
        <span className="list-kpi-value">{cuenta.revocadas}</span>
        <span className="list-kpi-label">revocadas</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterLinked('yes'); }} title="Ver las que ya tienen dispositivo asignado">
        <span className="list-kpi-value">{cuenta.vinculadas}</span>
        <span className="list-kpi-label">con dispositivo</span>
      </button>
    </div>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: 'Agente',
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{c.name || 'Sin nombre'}</span>
            <span className="list-hint list-code">{c.agent_id}</span>
          </span>
        ),
      },
      {
        key: 'association',
        label: 'Dispositivo',
        sortable: true,
        sortValue: (c) => c.association?.ci_name || '',
        render: (c) => {
          const tipo = c.association?.ci_type || c.ci_type;
          const id = c.association?.ci_id || c.ci_id;
          const nombre = c.association?.ci_name;
          if (!id) return <span className="list-hint">Sin dispositivo asignado</span>;
          const ruta = docElementPath(tipo, id);
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: ruta ? 'pointer' : 'default' }}
              onClick={(event) => { event.stopPropagation(); if (ruta) navigate(ruta); }}
              title={ruta ? 'Ver el dispositivo' : 'Este tipo no tiene pantalla propia'}
            >
              {nombre || `${ciTypeLabel(tipo)} #${id}`}
            </button>
          );
        },
      },
      {
        key: 'is_active',
        label: 'Estado',
        sortable: true,
        sortValue: (c) => (c.is_active ? 1 : 0),
        render: (c) => (c.is_active
          ? <span className="list-badge list-badge--on">Activa</span>
          : <span className="list-badge list-badge--off">Revocada</span>),
      },
      {
        key: 'last_used_at',
        label: 'Último uso',
        sortable: true,
        render: (c) => (c.last_used_at
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{new Date(c.last_used_at).toLocaleDateString()}</span>
              <span className="list-hint">{hace(c.last_used_at)}</span>
            </span>
          )
          : <span className="list-hint">Nunca</span>),
      },
      {
        key: 'key_prefix',
        label: 'Prefijo',
        sortable: true,
        render: (c) => <span className="list-code">{c.key_prefix}…</span>,
      },
      {
        key: 'ci_type',
        label: 'Tipo de dispositivo',
        sortable: true,
        sortValue: (c) => ciTypeLabel(c.association?.ci_type || c.ci_type),
        render: (c) => {
          const tipo = c.association?.ci_type || c.ci_type;
          return tipo ? <span className="list-badge">{ciTypeLabel(tipo)}</span> : dash;
        },
      },
      {
        key: 'created_at',
        label: 'Creada',
        sortable: true,
        render: (c) => (c.created_at ? new Date(c.created_at).toLocaleDateString() : dash),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const defaultVisible = ['name', 'association', 'is_active', 'last_used_at', 'key_prefix'];

  const filters = [
    {
      key: 'status', label: 'Estado', allLabel: 'Activas y revocadas',
      value: filterStatus, onChange: setFilterStatus,
      options: [
        { value: 'active', label: 'Solo las activas' },
        { value: 'revoked', label: 'Solo las revocadas' },
      ],
    },
    {
      key: 'linked', label: 'Dispositivo', allLabel: 'Con o sin dispositivo',
      value: filterLinked, onChange: setFilterLinked,
      options: [
        { value: 'yes', label: 'Con dispositivo asignado' },
        { value: 'no', label: 'Sin dispositivo asignado' },
      ],
    },
    {
      key: 'citype', label: 'Tipo de dispositivo', allLabel: 'Cualquier tipo',
      value: filterCiType, onChange: setFilterCiType,
      options: ciTypeOptions,
    },
    {
      key: 'group', label: 'Agrupar por', allLabel: 'Sin agrupar',
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'type', label: 'Tipo de dispositivo' },
        { value: 'status', label: 'Estado' },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'type') return (c) => ciTypeLabel(c.association?.ci_type || c.ci_type);
    if (groupMode === 'status') return (c) => (c.is_active ? 'Activas' : 'Revocadas');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode]);

  const exportRows = useMemo(() => filtered.map((c) => ({
    'Agente': c.name || '',
    'ID del agente': c.agent_id || '',
    'Dispositivo': c.association?.ci_name || c.association?.display || '',
    'Tipo': ciTypeLabel(c.association?.ci_type || c.ci_type),
    'Estado': c.is_active ? 'Activa' : 'Revocada',
    'Prefijo': c.key_prefix || '',
    'Último uso': c.last_used_at ? new Date(c.last_used_at).toLocaleString() : 'Nunca',
    'Creada': c.created_at ? new Date(c.created_at).toLocaleString() : '',
  })), [filtered]);

  const subtitulo = `${credentials.length} credenciales · ${filtered.length} filtradas`;

  return (
    <>
      <ListPage
        icon={KeyIcon}
        title="Credenciales del agente"
        subtitle={subtitulo}
        // Vive dentro de las pestañas de «Agente CMDB», que ya tienen su propio
        // título: aquí la cabecera baja a sección (h2) en vez de competir con él.
        embedded

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por agente, dispositivo o prefijo…"
        filters={filters}

        exportRows={exportRows}
        exportFileBase="credenciales-agente"
        exportSheetName="Credenciales"

        columns={columns}
        columnStorageKey="cmdb_cols_agent_credentials"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(c) => c.id}
        rowTitle={(c) => c.name || 'Credencial'}
        groupBy={groupBy}

        kpis={kpis}

        headerActions={(
          <>
            {notice && <span className="list-badge list-badge--on">{notice}</span>}
            <button type="button" className="list-btn-secondary" onClick={() => setKeysOpen(true)}>
              <KeyIcon style={{ width: 15, height: 15 }} /> Claves de empresa ({companyKeys.length})
            </button>
          </>
        )}

        views={[
          { key: 'list', label: 'Lista', icon: ListBulletIcon, title: 'Tabla con todas las columnas' },
          {
            key: 'card', label: 'Fichas', icon: Squares2X2Icon,
            title: 'Tarjetas con lo esencial de cada credencial',
            render: ({ rows, rowActions }) => (
              <div className="list-cards">
                {rows.map((c) => (
                  <div key={c.id} className="list-card">
                    <div className="list-card-head">
                      <div>
                        <div className="list-card-title">{c.name || 'Sin nombre'}</div>
                        <span className="list-hint list-code">{c.agent_id}</span>
                      </div>
                      {c.is_active
                        ? <span className="list-badge list-badge--on">Activa</span>
                        : <span className="list-badge list-badge--off">Revocada</span>}
                    </div>
                    <div className="list-card-meta">
                      {c.association?.ci_id
                        ? <span className="list-badge">{c.association.ci_name || c.association.display}</span>
                        : <span className="list-hint">Sin dispositivo asignado</span>}
                      <span className="list-code">{c.key_prefix}…</span>
                      {c.last_used_at && <span className="list-hint">Último uso {hace(c.last_used_at)}</span>}
                    </div>
                    <div className="list-card-actions">{rowActions(c)}</div>
                  </div>
                ))}
              </div>
            ),
          },
        ]}
        listKey="agent_credentials"
        defaultView="list"

        loading={loading}
        loadingText="Cargando credenciales…"
        error={error}

        empty={{
          icon: '🔑',
          title: credentials.length === 0
            ? 'Todavía no hay credenciales de agente'
            : 'Ninguna credencial coincide con los filtros',
          subtitle: credentials.length === 0
            ? 'Cada agente recibe la suya al instalarse (por bootstrap) o al reemitirla desde la pestaña Bootstrap.'
            : undefined,
          action: credentials.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={() => setKeysOpen(true)}>
              <KeyIcon style={{ width: 14, height: 14 }} /> Claves de empresa
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (c) => (
            <>
              <button
                type="button"
                className="list-action-btn"
                disabled={!c.is_active || pendingAction === `revoke-${c.id}`}
                onClick={() => revocarCredencial(c)}
                title={c.is_active
                  ? 'Revocar la credencial (deja de autenticar; se conserva el rastro)'
                  : 'Ya está revocada'}
              >
                <NoSymbolIcon />
              </button>
              <button
                type="button"
                className="list-action-btn"
                disabled={!c.agent_id}
                onClick={() => {
                  navigator.clipboard?.writeText(c.agent_id || '');
                  aviso('ID del agente copiado');
                }}
                title="Copiar el ID del agente al portapapeles"
              >
                <ClipboardDocumentIcon />
              </button>
              {c.association?.ci_id && docElementPath(c.association.ci_type, c.association.ci_id) && (
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(docElementPath(c.association.ci_type, c.association.ci_id))}
                  title="Ver el dispositivo asignado"
                >
                  <ServerStackIcon />
                </button>
              )}
            </>
          ),
          canDelete: (c) => c.is_active === false,
          onDelete: borrarCredencial,
          deleteTitle: 'Borrar credencial revocada',
          deleteMessage: (c) => (
            <>
              ¿Seguro que quieres borrar la credencial <strong>{c.name || c.agent_id}</strong>?{' '}
              Es definitivo: solo se ofrece en las que ya están revocadas y se pierde su prefijo, aunque
              el histórico de eventos se conserva.
            </>
          ),
        }}
      />

      {/* ══ Diálogo: claves de empresa ══ */}
      {keysOpen && (
        <Dialog
          title="Claves de empresa"
          icon={<KeyIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          onClose={() => { setKeysOpen(false); setRawKey(''); }}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => { setKeysOpen(false); setRawKey(''); }}>
              Cerrar
            </button>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Una clave de empresa autoriza instalaciones del agente, pero <strong>no</strong> queda atada a
            un dispositivo: eso lo dice la credencial individual que el agente recibe al darse de alta.
          </p>

          {rawKey && (
            <div
              style={{
                background: 'var(--amber-50, rgba(245,158,11,.12))',
                border: '1px solid var(--amber-600, #d97706)',
                borderRadius: 'var(--radius-sm)',
                padding: '10px 12px',
                marginBottom: 12,
              }}
            >
              <strong style={{ fontSize: '0.85rem' }}>Cópiala ahora: no se vuelve a mostrar.</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <span className="list-code" style={{ wordBreak: 'break-all' }}>{rawKey}</span>
                <button
                  type="button"
                  className="list-btn-secondary"
                  style={{ padding: '3px 10px', fontSize: '0.75rem' }}
                  onClick={() => navigator.clipboard?.writeText(rawKey)}
                >
                  <ClipboardDocumentIcon style={{ width: 13, height: 13 }} /> Copiar
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
            <label className="list-hint" style={{ flex: 1, minWidth: 220 }}>
              Nombre de la nueva clave
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') crearClave(); }}
                placeholder="ej. prod-server-01"
              />
            </label>
            <button
              type="button"
              className="list-btn-primary"
              onClick={crearClave}
              disabled={creatingKey || !newKeyName.trim()}
            >
              <PlusIcon style={{ width: 14, height: 14 }} />
              {creatingKey ? ' Generando…' : ' Generar clave'}
            </button>
          </div>

          {companyKeys.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              Todavía no hay claves de empresa. Genera una para autorizar instalaciones del agente.
            </p>
          )}

          {companyKeys.map((key) => (
            <div
              key={key.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '7px 0', borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ fontWeight: 600, minWidth: 150 }}>{key.name}</span>
              <span className="list-code">{key.key_prefix}…</span>
              {key.is_active
                ? <span className="list-badge list-badge--on">Activa</span>
                : <span className="list-badge list-badge--off">Revocada</span>}
              <span className="list-hint">
                {key.last_used_at ? `Último uso ${hace(key.last_used_at)}` : 'Sin usar todavía'}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {key.is_active ? (
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={pendingAction === `key-revoke-${key.id}`}
                    onClick={() => revocarClave(key)}
                    title="Revocar la clave (deja de autorizar instalaciones)"
                  >
                    <NoSymbolIcon />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={pendingAction === `key-delete-${key.id}`}
                    onClick={() => borrarClave(key)}
                    title="Borrar la clave revocada (definitivo)"
                  >
                    <NoSymbolIcon style={{ color: 'var(--red-600)' }} />
                  </button>
                )}
              </span>
            </div>
          ))}
        </Dialog>
      )}
    </>
  );
}
